import React from "react";

import { useDispatch, useSelector } from "react-redux";
import { layerActions } from "../../store";

import { Box, Switch, Typography } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

import LayerGroupArrow from "../UI/LayerGroupArrow";
import PointLayerLegend from "./PointLayerLegend";
import PropertyControl from "./PropertyControl";
import StyledTooltip from "../UI/StyledTooltip";

import { styles } from "../../config/config.theme";

import classes from "./LayerControl.module.css";

function LayerControl({ id }) {
  const dispatch = useDispatch();
  const layerName = useSelector((state) => state.layers[id].name);
  const showLayer = useSelector((state) => state.layers[id].showLayer);
  const pointCount = useSelector((state) => state.layers[id].pointCount);
  const showLayerControl = useSelector((state) =>
    state.layers[id].showLayerControl
      ? state.layers[id].showLayerControl
      : false
  );
  const colorValue = useSelector((state) => state.layers[id].colorValue);
  const layerProperties = useSelector((state) => state.layers[id].properties);
  const layerPropertyGroups = useSelector(
    (state) => state.layers[id].propertyGroups
  );
  const activeFilters = useSelector(
    (state) => state.layers[id].activeFilters || {}
  );

  const dataName = useSelector((state) => state.layers[id].data);
  const dataState = useSelector((state) => state.data[dataName]);

  const handleToggleLayerControl = () => {
    dispatch(layerActions.toggleLayerControl({ id: id }));
  };

  const handleToggleLayer = () => {
    dispatch(layerActions.toggleLayer({ id: id }));

    // close all layer controls except the active layer
    dispatch(layerActions.toggleLayerControl({ id: id }));
  };

  const onPropertyGroupClick = (groupId) => {
    dispatch(
      layerActions.togglePropertyGroupVisibility({
        layerId: id,
        groupId,
      })
    );
  };

  const filterCount = Object.keys(activeFilters || {}).length;

  return (
    <>
      <div
        className={`layer-control-header ${classes["layer-control-header"]}`}
      >
        <Box sx={{ display: "flex" }}>
          <LayerGroupArrow
            handleClick={handleToggleLayerControl}
            show={showLayerControl}
          />
          <Typography
            onClick={handleToggleLayerControl}
            className={classes["layer-name"]}
            sx={{
              width: pointCount && showLayer ? "190px" : "auto",
              fontSize: "0.9rem",
            }}
          >
            {layerName}
          </Typography>
          {pointCount && showLayer && (
            <StyledTooltip title={`Sites: ${pointCount.toLocaleString()}`}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 0.5,
                  borderRadius: "2px",
                  fontSize: "0.8rem",
                  backgroundColor: styles["background-secondary"],
                }}
              >
                {pointCount.toLocaleString()}
              </Box>
            </StyledTooltip>
          )}
          {showLayer && filterCount > 0 && (
            <StyledTooltip
              title={`Total filters: ${filterCount.toLocaleString()}`}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  m: 0,
                  ml: "10px",
                  pl: 0.5,
                  pr: 0.5,
                  backgroundColor: styles["background-secondary"],
                  borderRadius: "2px",
                  fontSize: "0.8rem",
                }}
              >
                <FilterAltIcon fontSize="1.5rem" color="primary" />
                {filterCount}
              </Box>
            </StyledTooltip>
          )}
        </Box>
        <Box sx={{ display: "flex" }}>
          {dataState.fetching && <div className={classes["loading-spinner"]} />}
          <Switch
            size="small"
            checked={showLayer}
            onClick={handleToggleLayer}
          />
        </Box>
      </div>
      <div
        className={classes["layer-control-container"]}
        style={{ display: showLayerControl ? "grid" : "none" }}
      >
        <PointLayerLegend id={id} color={"#000"} />
        {dataState.fetched ? (
          Object.keys(layerPropertyGroups).map((groupId, index) => {
            const groupProperties = layerProperties.filter(
              (p) => groupId === p.groupId
            );

            const filterCount = groupProperties.filter((f) =>
              Object.keys(activeFilters).includes(f.id)
            ).length;
            return (
              <React.Fragment key={groupId}>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  id={`filter-group-header-${index}`}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {layerProperties.find((p) => p.id === colorValue)
                      .groupId === groupId ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: 16,
                          width: 16,
                        }}
                      >
                        <Box
                          sx={{
                            height: "8px",
                            width: "8px",
                            borderRadius: "50%",
                            backgroundColor: "green",
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ height: 16, width: 16 }} />
                    )}
                    <LayerGroupArrow
                      handleClick={() => onPropertyGroupClick(groupId)}
                      show={layerPropertyGroups[groupId].isVisible}
                    />
                    <Typography
                      sx={{
                        color: "primary.contrastText",
                        fontSize: "0.9rem",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => onPropertyGroupClick(groupId)}
                    >
                      {layerPropertyGroups[groupId].label}
                    </Typography>
                  </Box>

                  {filterCount > 0 && (
                    <StyledTooltip
                      title={`Filters in this group: ${filterCount}`}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          pl: 0.5,
                          pr: 0.5,
                          borderRadius: "2px",
                          color: styles["text-primary"],
                          backgroundColor: styles["background-secondary"],
                          fontSize: "0.8rem",
                        }}
                      >
                        <FilterAltIcon fontSize="1.5rem" color="primary" />
                        {filterCount}
                      </Box>
                    </StyledTooltip>
                  )}
                </Box>
                {layerPropertyGroups[groupId].isVisible ? (
                  <div className={classes["filter-group-contents"]}>
                    {groupProperties.map((p, index) => (
                      <PropertyControl
                        key={p.id}
                        layerId={id}
                        propertyId={p.id}
                        orderId={`property-control-${index}`}
                      />
                    ))}
                  </div>
                ) : null}
              </React.Fragment>
            );
          })
        ) : (
          <div className={classes["download-data-message"]}>
            Toggle on {layerName} to see its property options.
          </div>
        )}
      </div>
    </>
  );
}

export default LayerControl;
